import React, { useState, useMemo, Suspense, useRef } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  CardRoot,
  CardBody,
  Spinner,
  Image,
  Link
} from '@chakra-ui/react';
import { SurveyData } from '../types/data';
import { createShortMapping, calculateRegressionLine, createClevelandDotPlotData, sortCategories, getSexoColors } from '../utils/dataUtils';

// Lazy load heavy components
const DemographicSelector = React.lazy(() => import('./DemographicSelector'));
const PieChartSection = React.lazy(() => import('./PieChartSection'));
const AnalysisControls = React.lazy(() => import('./AnalysisControls'));
const VisualizationSection = React.lazy(() => import('./VisualizationSection'));
const DynamicInfoCards = React.lazy(() => import('./DynamicInfoCards'));

interface DashboardProps {
  data: SurveyData[];
}

const Dashboard: React.FC<DashboardProps> = ({ data }) => {
  const [selectedQuestion, setSelectedQuestion] = useState<string>('q1_salud_mental_bienestar');
  const [selectedAnalysis, setSelectedAnalysis] = useState<string>('distribution');
  const [selectedDemographic, setSelectedDemographic] = useState<string>('sexo');
  const [correlationQuestion1, setCorrelationQuestion1] = useState<string>('q1_salud_mental_bienestar');
  const [correlationQuestion2, setCorrelationQuestion2] = useState<string>('q2_pastores_identificar_trastornos');
  const [dotYAxisDemo, setDotYAxisDemo] = useState<string>('nivel_educativo');
  const [dotColorDemo, setDotColorDemo] = useState<string>('sexo');
  const [dotOrientation, setDotOrientation] = useState<'horizontal' | 'vertical'>('horizontal');
  const [barOrientation, setBarOrientation] = useState<'horizontal' | 'vertical'>('vertical');
  const [pieChartDemographic, setPieChartDemographic] = useState<string>('sexo');
  
  const visualizationRef = useRef<HTMLDivElement>(null);

  // Function to scroll to visualization (can be called manually)
  const scrollToVisualization = () => {
    if (visualizationRef.current) {
      setTimeout(() => {
        visualizationRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }, 100);
    }
  };

  // Loading component for Suspense fallback
  const LoadingSpinner = () => (
    <Box display="flex" alignItems="center" justifyContent="center" minHeight="200px">
      <Spinner size="lg" color="#1F77B4" />
    </Box>
  );

  // Color scheme
  const colors = useMemo(() => ({
    navyBlue: '#1F77B4',
    teal: '#17BECF',
    burntOrange: '#FF7F0E',
    slateGray: '#7F7F7F',
    lightGray: '#f8f9fa',
    white: '#ffffff',
    darkText: '#2d3748',
    mutedText: '#718096'
  }), []);

  const questionMapping = createShortMapping();

  // Plot colors
  const plotColors = useMemo(() => [
    colors.navyBlue,
    colors.teal,
    colors.burntOrange,
    colors.slateGray,
    '#2E8B57',
    '#8A2BE2',
    '#FF6347',
    '#20B2AA',
    '#FF8C00',
    '#9370DB',
    '#3CB371',
    '#FF69B4'
  ], [colors]);

  // Demographic options
  const demographicOptions = {
    sexo: 'Sexo',
    edad: 'Edad',
    nivel_educativo: 'Nivel Educativo',
    experiencia_pastoral: 'Experiencia Pastoral',
    programa_iglesia: 'Programa de Iglesia'
  };

  // Generate demographic pie chart data
  const demographicPieData: any = useMemo(() => {
    const demographic = pieChartDemographic;
    const categories = Array.from(new Set(data.map(d => d[demographic as keyof SurveyData]).filter(Boolean)));
    
    // Sort categories using centralized sorting utility
    const sortedCategories = sortCategories(categories as string[], demographic);
    
    const values = sortedCategories.map(cat => data.filter(d => d[demographic as keyof SurveyData] === cat).length);
    const total = values.reduce((sum, val) => sum + val, 0);
    
    // Calculate percentages and determine which slices should show text
    const percentages = values.map(val => (val / total) * 100);
    const textArray = percentages.map(percent => percent >= 5 ? `${percent.toFixed(1)}%` : '');
    
    // Use custom colors for sexo demographic, otherwise use default plotColors
    const colorsToUse = demographic === 'sexo' ? getSexoColors(sortedCategories) : plotColors;
    
    return [{
      labels: sortedCategories,
      values: values,
      type: 'pie',
      hole: 0.4,
      text: textArray,
      textinfo: 'text',
      textposition: 'auto',
      textfont: {
        size: 10,
        color: 'white'
      },
      marker: {
        colors: colorsToUse
      }
    }];
  }, [data, pieChartDemographic, plotColors]);

  // Conditional data generation - only calculate what's needed
  const visualizationData = useMemo(() => {
    switch (selectedAnalysis) {
      case 'distribution':
        const demographic = selectedDemographic;
        const categories = Array.from(new Set(data.map(d => d[demographic as keyof SurveyData]).filter(Boolean)));
        
        // Sort categories using centralized sorting utility
        const sortedCategories = sortCategories(categories as string[], demographic);
        const totalRespondents = data.length;
        
        // Use custom colors for sexo demographic, otherwise use default plotColors
        const colorsToUse = demographic === 'sexo' ? getSexoColors(sortedCategories) : plotColors;
        
        const traces = sortedCategories.map((cat, index) => {
          const catData = data.filter(d => d[demographic as keyof SurveyData] === cat);
          
          const distribution = [1, 2, 3, 4, 5].map(score => {
            const count = catData.filter(d => Number(d[selectedQuestion as keyof SurveyData]) === score).length;
            return totalRespondents > 0 ? (count / totalRespondents) * 100 : 0;
          });

          const scaleLabels = ['1 Totalmente en desacuerdo', '2 En desacuerdo', '3 Neutral', '4 De acuerdo', '5 Totalmente de acuerdo'];
          
          return {
            x: barOrientation === 'horizontal' ? distribution : scaleLabels,
            y: barOrientation === 'horizontal' ? scaleLabels : distribution,
            type: 'bar',
            orientation: barOrientation === 'horizontal' ? 'h' : 'v',
            name: String(cat),
            marker: {
              color: colorsToUse[index % colorsToUse.length]
            },
          };
        });

        // Calculate total percentages for annotations
        const scaleLabels = ['1 Totalmente en desacuerdo', '2 En desacuerdo', '3 Neutral', '4 De acuerdo', '5 Totalmente de acuerdo'];
        const barAnnotations = scaleLabels.map((label: string, index: number) => {
          const totalForThisBar = traces.reduce((sum: number, trace: any) => {
            const value = barOrientation === 'horizontal' ? trace.x[index] : trace.y[index];
            return sum + (Number(value) || 0);
          }, 0);
          
          return {
            x: barOrientation === 'horizontal' ? totalForThisBar + 2 : index,
            y: barOrientation === 'horizontal' ? index : totalForThisBar + 2,
            text: `${totalForThisBar.toFixed(1)}%`,
            showarrow: false,
            font: {
              size: 12,
              color: colors.darkText
            },
            xref: barOrientation === 'horizontal' ? 'x' : 'x',
            yref: barOrientation === 'horizontal' ? 'y' : 'y'
          };
        });

        return {
          stackBarData: traces,
          correlationData: null,
          dotChartData: null,
          barAnnotations: barAnnotations
        };

      case 'correlation':
        try {
          const demographic = selectedDemographic;
          const categories = Array.from(new Set(data.map(d => d[demographic as keyof SurveyData]).filter(val => 
            val !== null && val !== undefined && String(val).trim() !== '' && val !== 'NA'
          )));
          
          // Sort categories using centralized sorting utility
          const sortedCategories = sortCategories(categories as string[], demographic);
          
          // Use custom colors for sexo demographic, otherwise use default plotColors
          const colorsToUse = demographic === 'sexo' ? getSexoColors(sortedCategories) : plotColors;
          
          const traces = sortedCategories.map((cat, index) => {
            const catData = data.filter(d => {
              const demographicValue = d[demographic as keyof SurveyData];
              const q1Value = d[correlationQuestion1 as keyof SurveyData];
              const q2Value = d[correlationQuestion2 as keyof SurveyData];
              
              return demographicValue === cat &&
                     q1Value !== null && 
                     q2Value !== null &&
                     q1Value !== undefined &&
                     q2Value !== undefined &&
                     String(q1Value).trim() !== '' &&
                     String(q2Value).trim() !== '' &&
                     !isNaN(Number(q1Value)) &&
                     !isNaN(Number(q2Value)) &&
                     Number(q1Value) >= 1 &&
                     Number(q1Value) <= 5 &&
                     Number(q2Value) >= 1 &&
                     Number(q2Value) <= 5;
            });

            if (catData.length < 2) return null;

            try {
              const regressionLine = calculateRegressionLine(catData, correlationQuestion1, correlationQuestion2);
              
              if (!regressionLine || regressionLine.length < 2) return null;

              // Additional validation to ensure regression line has valid data
              const validPoints = regressionLine.filter((p: any) => 
                !isNaN(p.x) && !isNaN(p.y) && isFinite(p.x) && isFinite(p.y)
              );

              if (validPoints.length < 2) return null;

              return {
                x: validPoints.map((p: any) => p.x),
                y: validPoints.map((p: any) => p.y),
                mode: 'lines',
                type: 'scatter',
                name: String(cat),
                line: {
                  color: colorsToUse[index % colorsToUse.length],
                  width: 3
                }
              };
            } catch (error) {
              console.warn(`Error calculating regression line for ${demographic} category "${cat}":`, error);
              return null;
            }
          }).filter(Boolean);

          return {
            stackBarData: null,
            correlationData: traces.length > 0 ? traces : null,
            dotChartData: null
          };
        } catch (error) {
          console.error('Error in correlation calculation:', error);
          return {
            stackBarData: null,
            correlationData: null,
            dotChartData: null
          };
        }

      case 'dot':
        try {
          return {
            stackBarData: null,
            correlationData: null,
            dotChartData: createClevelandDotPlotData(data, selectedQuestion, dotYAxisDemo, dotColorDemo)
          };
        } catch (error) {
          console.error('Error generating Cleveland dot plot data:', error);
          return {
            stackBarData: null,
            correlationData: null,
            dotChartData: null
          };
        }

      default:
        return {
          stackBarData: null,
          correlationData: null,
          dotChartData: null
        };
    }
  }, [data, selectedAnalysis, selectedQuestion, selectedDemographic, correlationQuestion1, correlationQuestion2, dotYAxisDemo, dotColorDemo, barOrientation, plotColors, colors.darkText]);

  const { stackBarData, correlationData, dotChartData, barAnnotations } = visualizationData;

  const layout = {
    font: { family: 'Inter, system-ui, sans-serif' },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    margin: { t: 40, r: 20, b: 60, l: 60 },
    showlegend: true,
    legend: {
      orientation: 'h' as const,
      y: -0.2
    }
  };

  return (
    <Box width="100%" maxWidth="100%" overflowX="hidden" minH="100vh">
      {/* Collaborative Logo Header */}
      <Box 
        bg="white" 
        borderBottom="1px solid" 
        borderColor="gray.200" 
        py={6} 
        width="100%" 
        maxWidth="100%"
      >
        <Container maxW="container.xl" width="100%" px={{ base: 4, md: 6 }}>
          <VStack gap={4}>
            <HStack 
              gap={{ base: 6, md: 12 }} 
              align="center" 
              justify="center"
              flexWrap="wrap"
              py={2}
            >
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="center"
                maxW={{ base: "140px", md: "180px" }}
                flex="1"
                minW="120px"
              >
                <Image 
                  src="/fina_logo_bundle-27.png" 
                  alt="FINA CONSULTING GROUP" 
                  maxH={{ base: "70px", md: "90px" }}
                  objectFit="contain"
                  filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
                />
              </Box>
              
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="center"
                px={4}
                position="relative"
              >
                <Box
                  width="2px"
                  height="40px"
                  bg="gray.300"
                  borderRadius="1px"
                  mx={2}
                />
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  width="12px"
                  height="12px"
                  borderRadius="50%"
                  bg="#1F77B4"
                  border="2px solid white"
                  boxShadow="0 2px 8px rgba(0,0,0,0.15)"
                />
              </Box>
              
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="center"
                maxW={{ base: "140px", md: "180px" }}
                flex="1"
                minW="120px"
              >
                <Image 
                  src="/ITGroup_Flat.png" 
                  alt="ITGROUP Technology consultants, Inc." 
                  maxH={{ base: "70px", md: "90px" }}
                  objectFit="contain"
                  filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
                />
              </Box>
            </HStack>
            
            <Text 
              className="typography-body-sm"
              color="gray.600" 
              textAlign="center" 
              fontWeight="medium"
              letterSpacing="wide"
            >
              En colaboración FINA Consulting Group y ITGROUP Technology consultants, Inc.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Professional Header */}
      <Box className="professional-header" py={6} mb={6} width="100%" maxWidth="100%">
        <Container maxW="container.xl" width="100%" px={{ base: 4, md: 6 }} className="professional-header-content">
          <VStack gap={6}>
            <Heading 
              className="professional-title typography-h1"
              textAlign="center"
              px={2}
            >
              Análisis de Salud Mental Pastoral
            </Heading>
            <Text 
              className="professional-subtitle typography-body-lg"
              textAlign="center"
              maxW="2xl"
              px={2}
            >
              Dashboard interactivo de percepción pastoral sobre salud mental y adicciones
            </Text>
            <Box className="professional-accent" />
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.xl" width="100%" px={{ base: 4, md: 6 }}>
        <VStack gap={6} align="stretch">

          {/* Pie Chart Section */}
          <CardRoot className="professional-card">
            <CardBody p={{ base: 6, md: 8 }}>
              <VStack gap={6}>
                <Suspense fallback={<LoadingSpinner />}>
                  <DemographicSelector
                    selectedDemographic={pieChartDemographic}
                    onDemographicChange={setPieChartDemographic}
                    demographicOptions={demographicOptions}
                    colors={colors}
                  />
                </Suspense>
                
                <Box 
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  minHeight={{ base: "320px", md: "450px" }}
                  width="100%"
                  mb={{ base: 8, md: 12 }}
                >
                  <Suspense fallback={<LoadingSpinner />}>
                    <PieChartSection
                      demographicPieData={demographicPieData}
                      pieChartDemographic={pieChartDemographic}
                      demographicOptions={demographicOptions}
                      plotColors={plotColors}
                      colors={colors}
                      layout={layout}
                    />
                  </Suspense>
                </Box>

                {/* Dynamic Information Cards - Now under pie chart */}
                <Suspense fallback={<LoadingSpinner />}>
                  <DynamicInfoCards
                    data={data}
                    selectedDemographic={pieChartDemographic}
                    demographicOptions={demographicOptions}
                    plotColors={plotColors}
                    colors={colors}
                  />
                </Suspense>
              </VStack>
            </CardBody>
          </CardRoot>

          {/* Analysis Controls */}
          <CardRoot className="professional-card analysis-controls-card">
            <CardBody p={{ base: 6, md: 8 }}>
              <Suspense fallback={<LoadingSpinner />}>
                <AnalysisControls
                  selectedAnalysis={selectedAnalysis}
                  onAnalysisChange={setSelectedAnalysis}
                  selectedQuestion={selectedQuestion}
                  onQuestionChange={setSelectedQuestion}
                  selectedDemographic={selectedDemographic}
                  onDemographicChange={setSelectedDemographic}
                  barOrientation={barOrientation}
                  onBarOrientationChange={setBarOrientation}
                  correlationQuestion1={correlationQuestion1}
                  onCorrelationQuestion1Change={setCorrelationQuestion1}
                  correlationQuestion2={correlationQuestion2}
                  onCorrelationQuestion2Change={setCorrelationQuestion2}
                  dotYAxisDemo={dotYAxisDemo}
                  onDotYAxisDemoChange={setDotYAxisDemo}
                  dotColorDemo={dotColorDemo}
                  onDotColorDemoChange={setDotColorDemo}
                  dotOrientation={dotOrientation}
                  onDotOrientationChange={setDotOrientation}
                  questionMapping={questionMapping}
                  demographicOptions={demographicOptions}
                  colors={colors}
                  onScrollToVisualization={scrollToVisualization}
                />
              </Suspense>
            </CardBody>
          </CardRoot>

          {/* Visualization */}
          <CardRoot ref={visualizationRef} className="professional-card visualization-section-card">
            <CardBody p={{ base: 6, md: 8 }}>
              <Box minHeight={{ base: "700px", md: "800px" }}>
                <Suspense fallback={<LoadingSpinner />}>
                  <VisualizationSection
                    selectedAnalysis={selectedAnalysis}
                    stackBarData={stackBarData}
                    correlationData={correlationData}
                    dotChartData={dotChartData}
                    barAnnotations={barAnnotations}
                    barOrientation={barOrientation}
                    correlationQuestion1={correlationQuestion1}
                    correlationQuestion2={correlationQuestion2}
                    selectedQuestion={selectedQuestion}
                    dotYAxisDemo={dotYAxisDemo}
                    dotColorDemo={dotColorDemo}
                    dotOrientation={dotOrientation}
                    questionMapping={questionMapping}
                    demographicOptions={demographicOptions}
                    layout={layout}
                    colors={colors}
                  />
                </Suspense>
              </Box>
            </CardBody>
          </CardRoot>

          {/* Visualization Report - Hidden */}
          {/* <CardRoot 
            mt={{ 
              base: selectedAnalysis === 'dot' ? 20 : 8, 
              md: selectedAnalysis === 'dot' ? 20 : 8 
            }}
            mb={{ base: 12, md: 4 }}
            className="professional-card"
          >
            <Suspense fallback={<LoadingSpinner />}>
              <VisualizationReport
                selectedAnalysis={selectedAnalysis}
                data={data}
                selectedQuestion={selectedQuestion}
                selectedDemographic={selectedDemographic}
                correlationQuestion1={correlationQuestion1}
                correlationQuestion2={correlationQuestion2}
                dotYAxisDemo={dotYAxisDemo}
                dotColorDemo={dotColorDemo}
                questionMapping={questionMapping}
                demographicOptions={demographicOptions}
                colors={colors}
              />
            </Suspense>
          </CardRoot> */}

          {/* QR Code Section */}
          <CardRoot className="professional-card">
            <CardBody p={{ base: 6, md: 8 }}>
              <VStack gap={6} align="center">
                {/* Main heading */}
                <VStack gap={4} align="center" maxW="600px">
                  <Heading 
                    className="typography-h2"
                    color="gray.800" 
                    textAlign="center"
                    fontWeight="bold"
                    fontSize={{ base: "2xl", md: "3xl" }}
                  >
                    Únete a Nuestra Investigación
                  </Heading>
                  <Text 
                    className="typography-body-xl"
                    color="gray.600" 
                    textAlign="center" 
                    lineHeight="1.7"
                    maxW="500px"
                    fontSize={{ base: "lg", md: "xl" }}
                  >
                    Tu perspectiva es importante para comprender la salud mental pastoral
                  </Text>
                </VStack>

                {/* QR Code Card */}
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  p={{ base: 6, md: 8 }}
                  bg="white"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.200"
                  maxW="400px"
                  width="100%"
                  boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                >
                  <VStack gap={4} align="center">
                    {/* QR Code */}
                    <Image 
                      src="/encuesta_qrcode.png" 
                      alt="QR Code para encuesta" 
                      maxH={{ base: "180px", md: "200px" }}
                      maxW={{ base: "180px", md: "200px" }}
                      objectFit="contain"
                      borderRadius="md"
                    />

                    {/* Call to action text */}
                    <VStack gap={4} align="center" maxW="400px">
                      <Text 
                        className="typography-body-xl"
                        color="gray.800" 
                        textAlign="center" 
                        fontWeight="semibold"
                        lineHeight="1.6"
                        fontSize={{ base: "lg", md: "xl" }}
                      >
                        Cada perspectiva cuenta
                      </Text>
                      <Text 
                        className="typography-body-lg"
                        color="gray.600" 
                        textAlign="center" 
                        lineHeight="1.7"
                        fontSize={{ base: "md", md: "lg" }}
                      >
                        Escanea el QR y sé parte de esta iniciativa sobre salud mental y adicciones.
                      </Text>
                    </VStack>

                    {/* Direct link option */}
                    <Link
                      href="https://docs.google.com/forms/d/e/1FAIpQLSdSKK6W6OjLeX9Sc5uklyETJoqPTcx_uFEjXo_7uBLgQ31ykw/viewform?usp=header"
                      target="_blank"
                      rel="noopener noreferrer"
                      display="flex"
                      alignItems="center"
                      gap={2}
                      px={6}
                      py={3}
                      bg="#1F77B4"
                      color="white"
                      borderRadius="lg"
                      textDecoration="none"
                      fontWeight="medium"
                      transition="all 0.2s ease"
                      _hover={{
                        bg: "#1565C0",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(31, 119, 180, 0.3)",
                        textDecoration: "none"
                      }}
                      _active={{
                        transform: "translateY(0)"
                      }}
                    >
                      <Text 
                        className="typography-body-md"
                        fontSize={{ base: "sm", md: "md" }}
                      >
                        O Haz Click aquí 
                      </Text>
                    </Link>
                  </VStack>
                </Box>
              </VStack>
            </CardBody>
          </CardRoot>

        </VStack>
      </Container>
      
      {/* Bottom spacing for mobile */}
      <Box height={{ base: "40px", md: "60px" }} />
    </Box>
  );
};

export default Dashboard;
